import Badge from "@/components/core/badge";
import type { CaseActivity } from "@/features/case-evaluation/types/activities";
import { formatDate } from "@/utils/format-date";
import { IconArrowBack, IconCalendarCheck, IconScale } from "@tabler/icons-react";
import clsx from "clsx";
import { useState } from "react";
import { useForm } from "react-hook-form";
import DataGrid, { type ColumnDef } from "../core/data-grid";
import { FileLinkWithViewer } from "../core/file-link-with-viewer";
import { Form } from "../core/forms/form";
import { HoverCard, HoverCardContent, HoverCardTrigger } from "../core/hover-card";

const MAX_DEPTH = 1;

const columns: ColumnDef<CaseActivity>[] = [
	{
		id: "title",
		label: "Title",
		cell: ({ data }) => <span>{data?.activity || "N/A"}</span>,
	},
	{
		id: "expunged_at",
		label: "Date Expunged",
		cell: ({ data }) => <span>{formatDate(data?.expunged_at)}</span>,
	},
	{
		id: "expunged_by",
		label: "Expunged By",
		cell: ({ data }) => <span>{data?.expunged_by}</span>,
	},
];

function ExpungeInfoCard({
	data,
}: {
	data: CaseActivity;
}) {
	const form = useForm({
		defaultValues: {
			id: 1,
		},
	});
	return (
		<Form {...form}>
			<form className="w-full">
				<div className="flex flex-col w-full p-4 gap-2">
					<div className="flex flex-col w-full rounded-md border border-border/50 overflow-hidden">
						<DataGrid variant="compact" data={data} columns={columns} />
					</div>
				</div>
			</form>
		</Form>
	);
}

const HistoryTreadItem = ({ title, activity, items = [], depth = 0 }: { title: string; depth?: number }) => {
	const [showReplies, setShowReplies] = useState(true);
	const hasChildren = depth < MAX_DEPTH;
	const isExpunged = activity?.status === "Expunged" || activity?.status === "Denied";
	const isGranted = activity?.status === "Granted";
	const isDraft = activity?.state === "draft";
	const isCancelled = activity?.hearing_status === "Cancelled" || activity?.hearing_status === "Rescheduled";

	return (
		<div className="flex flex-col w-full border-b last:border-b-0 border-dashed border-border overflow-hidden">
			<div className="flex flex-col w-full">
				<div className="flex flex-row items-center w-auto p-4 gap-2">
					<div
						className={clsx(
							"inline-flex shrink-0 self-start justify-center items-center w-[32px] h-[32px] rounded-full text-primary-foreground",
							activity?.receipient === "Bcc" || activity?.receipient === "Judge"
								? "bg-primary-500"
								: activity?.receipient === "Defendant"
									? "bg-red-500"
									: "bg-blue-500",
						)}
					>
						{/* {activity?.receipient === "Bcc" || activity?.receipient === "Judge" ? <IconScale size={18} stroke={2} /> : activity?.receipient?.charAt(0)}{" "} */}
						<div>
							{activity?.type === "Hearing" ? (
								<IconCalendarCheck size={18} stroke={2} className="bg-teal-500 w-[32px] h-[32px] text-white p-1.5 rounded-full" />
							) : (activity?.receipient === "Bcc" || activity?.receipient === "Judge") && activity?.type !== "Hearing" ? (
								<IconScale size={18} stroke={2} />
							) : (
								activity?.receipient?.charAt(0)
							)}
						</div>
					</div>

					<div className="flex flex-row w-full gap-2">
						<div className="flex flex-col w-full">
							<div className="inline-flex flex-row items-center gap-1">
								{isExpunged && (
									<Badge className="uppercase" size="xs" color="red-light">
										<HoverCard openDelay={300} closeDelay={300}>
											<HoverCardTrigger asChild>
												<button type="button">{activity?.status}</button>
											</HoverCardTrigger>
											<HoverCardContent>
												<ExpungeInfoCard data={activity} />
											</HoverCardContent>
										</HoverCard>
									</Badge>
								)}
								{isGranted && (
									<Badge className="uppercase" size="xs" color="green-light">
										{activity?.status}
									</Badge>
								)}
								{isCancelled && (
									<Badge className="uppercase" size="xs" color="red-light">
										{activity?.hearing_status}
									</Badge>
								)}
								{activity?.activity !== "Case Closed" && activity?.type === "Court Actions" && activity?.party_type === "Bcc" && !activity?.approved_at && (
									<Badge className="uppercase" size="xs" color="red-light">
										{activity?.approved_at ? "Approved" : "Pending Approval"}
									</Badge>
								)}
							</div>
							<div className="inline-flex flex-row items-center justify-between gap-1">
								<button
									type="button"
									onClick={() => {
										openPreviewModal(activity);
									}}
									className={
										activity?.status !== "Expunged" && activity?.hearing_status !== "Cancelled" && activity?.hearing_status !== "Rescheduled"
											? "font-semibold text-base text-foreground-dark"
											: "font-semibold text-base text-foreground-dark line-through"
									}
								>
									{isDraft && "Draft - "} {title}
								</button>
								{!isDraft && (
									<span className="text-base text-foreground/70">
										{activity?.filed_at && `${activity?.author?.full_name} Filed on ${formatDate(activity?.filed_at, "PPp")}`}
									</span>
								)}
							</div>
							<div className="text-base text-foreground/70">
								<div>
									{activity?.case_activity_users?.map((user, index) => (
										<div key={index} className=" items-center space-x-2 mr-1">
											{user.defendant?.user?.full_name || "No Name Available"}
										</div>
									))}
								</div>
								{activity?.with_fees === true && (
									<button
										type="button"
										className="cursor-pointer font-semibold"
										onClick={() => {
											openFeeModal(activity);
										}}
									>
										- with fees
									</button>
								)}
							</div>
							<div className="flex flex-col w-auto">
								{!isDraft && activity?.due_at && (
									<div className="inline-flex flex-row items-start gap-1">
										<span className="text-base text-foreground/70">{activity?.activity === "Hearing" ? "Scheduled" : "Due"}</span>
										<span className="text-base text-foreground-dark">{formatDate(activity?.due_at)}</span>
									</div>
								)}
								{!isDraft && activity?.ordered_at && activity?.type !== "Hearing" && (
									<div className="inline-flex flex-row items-start gap-1">
										<span className="text-base text-foreground/70">Date Ordered</span>
										<span className="text-base text-foreground-dark">{formatDate(activity?.ordered_at)}</span>
									</div>
								)}
								{!isDraft && activity?.scheduled_at && activity?.type !== "Hearing" && (
									<div className="inline-flex flex-row items-start gap-1">
										<span className="text-base text-foreground/70">Calendar Date</span>
										<span className="text-base text-foreground-dark">{formatDate(activity?.scheduled_at)}</span>
									</div>
								)}
								{!isDraft && activity?.service_date && activity?.type !== "Hearing" && (
									<div className="inline-flex flex-row items-start gap-1">
										<span className="text-base text-foreground/70">Date Served</span>
										<span className="text-base text-foreground-dark">{formatDate(activity?.service_date, "PP")}</span>
									</div>
								)}
								{!isDraft && activity?.submitted_at && (
									<div className="inline-flex flex-row items-start gap-1">
										<span className="text-base text-foreground/70">Date Submitted</span>
										<span className="text-base text-foreground-dark">{formatDate(activity?.submitted_at)}</span>
									</div>
								)}
								{/* <div className="inline-flex flex-row items-start gap-1">
                <span className="text-base text-foreground/70">{activity?.activity === "Hearing" ? "Activity/Event" : "Remarks"}: </span>
                <span className="text-base text-foreground-dark">{activity?.remarks || "-"}</span>
              </div> */}
								{!isDraft && activity?.attachments.length !== 0 && (
									<div className="flex flex-row flex-wrap gap-1.5 mt-2">
										{activity?.attachments.map((file, index) => (
											<FileLinkWithViewer key={index} file={file} url={file.original_url} maxLength={20} mime_type={""} />
										))}
									</div>
								)}
							</div>
						</div>
					</div>
				</div>

				<div className="flex flex-col w-full pl-10">
					{items.length > 0 && showReplies && depth < MAX_DEPTH && (
						<div className="flex flex-col w-full">
							{items.map((itemActivity, index) => (
								<HistoryTreadChild key={index} title={itemActivity?.activity} depth={depth + 1} activity={itemActivity} />
							))}
						</div>
					)}
				</div>
			</div>
		</div>
	);
};

const HistoryTreadChild = ({ title, depth, activity }: { title: string; depth: number }) => {
	return (
		<div className="flex flex-col w-full border-b last:border-b-0 border-dashed border-border">
			<HistoryTreadItem activity={activity} title={title} depth={depth} />
		</div>
	);
};

export const HistoryTread = ({ activity }) => {
	return (
		<div className="flex flex-col w-full rounded-md border border-border/50">
			<HistoryTreadItem activity={activity} items={activity?.replies} title={activity?.activity} />
		</div>
	);
};
